import { HttpRequestError } from "@workspace/base-api";

export interface PrintReceiptFailure {
	message: string;
	/** Retry is offered only where trying again can help: the printer being back, or the network. */
	retryable: boolean;
}

const UNREACHABLE = "Printer unreachable — try again";

/**
 * HA-361 AC4 — what the till says when a (re)print is refused. The server refuses before dispatching when the
 * receipt printer is not reachable (503 PRINTER_UNREACHABLE), and nothing is recorded, so Retry is safe and just
 * asks again. Other refusals say their own reason and offer no Retry: a missing permission or an unconfigured
 * printer does not fix itself.
 */
export function printReceiptFailure(error: unknown): PrintReceiptFailure {
	if (error instanceof HttpRequestError) {
		const status = error.response.status;
		const detail = error.problem?.detail;

		if (status === 403)
			return {
				message: "You don't have permission to print receipts",
				retryable: false,
			};
		if (status === 409 || status === 422)
			return {
				message: detail ?? "This terminal has no receipt printer available",
				retryable: false,
			};
		if (status === 503) return { message: UNREACHABLE, retryable: true };
		if (status === 404 || status === 400)
			return {
				message: detail ?? "This receipt can't be printed",
				retryable: false,
			};
	}

	// A dropped request: the till could not reach the server (nothing was dispatched), so asking again is safe.
	if (error instanceof TypeError)
		return { message: UNREACHABLE, retryable: true };

	return { message: "Couldn't print the receipt — try again", retryable: true };
}
